import { ApiClient } from '../client/ApiClient';
import { ApiResponse } from '../types';
import { AlgoliaProductHit } from './types/AlgoliaProductHit';
import algoliasearch, { SearchClient, SearchIndex } from 'algoliasearch';
import { createFetchRequester } from '@algolia/requester-fetch';
import { buildSearchFilters } from '@jbhi-fi/utils/src/buildAlgoliaSearchFilters/buildAlgoliaSearchFilters';
import { toCamelCase } from '@jbhi-fi/utils/src/toCamelCase';
import { createFallbackableCache } from '@algolia/cache-common';
import { createBrowserLocalStorageCache } from '@algolia/cache-browser-local-storage';
import { createInMemoryCache } from '@algolia/cache-in-memory';
import { ContentfulCollection } from '../contentful/types';
import {
  filterGoogleRecommendations,
  GoogleRecommendedItemWithVariance,
} from '../utils/googleRecommended/filterGoogleRecommendations';

export type GetSkusResponse = {
  products: Array<{ id: string; sku: string }>;
};

export type GetProductsResponse = {
  products: Array<ScoredAlgoliaHit>;
  numberOfProducts: number;
};

export type ScoredAlgoliaHit = {
  variance?: number;
  varianceScore?: number;
  googleScore?: number;
} & AlgoliaProductHit;

export type ProductClientConstructorArgs = {
  shopifyProductsGqlUrl: string;
  shopifyProductsGqlToken: string;
  algoliaAppId: string;
  algoliaSearchApiKey: string;
  algoliaSearchIndex: string;
  recommendationToken?: string;
  recommendationsEndpoint?: string;
  shouldExcludeMarketplaceProductsFromSuggestions?: boolean;
};

export type PartialProduct = {
  sku: string;
};

export type AlgoliaSearchOptions = Omit<
  Exclude<Parameters<SearchIndex['search']>[1], undefined> & {
    facetFilters?: ReadonlyArray<string>;
  },
  'filters'
>;

export type GetProductsOptions = {
  shouldResolveExtraOnlineProductFields?: boolean;
  shouldExcludeMarketplaceProducts?: boolean;
  sortByMethod?: Exclude<ContentfulCollection['fields'], undefined>['defaultSortMethod'];
  isPerks?: boolean;
  analyticsTags?: Array<string>;
  acceptedDeviationPercentage?: number;
  shouldEnhanceSuggestions?: boolean;
};

export class ProductClient extends ApiClient {
  private readonly _shopifyProductsGqlToken: string = '';
  private readonly _shopifyProductsGqlUrl: string = '';
  private _algoliaClient: SearchClient;
  private _algoliaSearchIndex: string;
  private _recommendationsEndpoint: string;
  private readonly _shouldExcludeMarketplaceProductsFromSuggestions: boolean;

  constructor({
    shopifyProductsGqlUrl,
    shopifyProductsGqlToken,
    algoliaAppId,
    algoliaSearchApiKey,
    algoliaSearchIndex,
    recommendationsEndpoint,
    shouldExcludeMarketplaceProductsFromSuggestions = false,
  }: ProductClientConstructorArgs) {
    super();
    this._shopifyProductsGqlToken = shopifyProductsGqlToken;
    this._shopifyProductsGqlUrl = shopifyProductsGqlUrl;
    this._algoliaClient = algoliasearch(algoliaAppId, algoliaSearchApiKey, { requester: createFetchRequester() });
    this._algoliaSearchIndex = algoliaSearchIndex;
    this._recommendationsEndpoint = recommendationsEndpoint ?? '';
    this._shouldExcludeMarketplaceProductsFromSuggestions = shouldExcludeMarketplaceProductsFromSuggestions;
  }

  parseAlgoliaHit = (hit: any): AlgoliaProductHit => {
    return {
      ...hit,
      title: hit.title ?? '',
      product_image: hit.product_image ?? '',
      primary_title: hit.primary_title ?? '',
      subtitle: hit.subtitle ?? '',
      price: hit.price ?? '',
      pricing: {
        ...(hit.pricing ?? {}),
        displayWasPrice: hit.pricing?.displayWasPrice ?? false,
        wasPrice: hit.pricing?.wasPrice ?? '',
      },
      availability: {
        ...(hit.availability ?? {}),
        canPreOrder: hit.availability?.canPreOrder ?? false,
        fulfilment: hit.availability?.fulfilment ?? '',
        productLifecycle: hit.availability?.productLifecycle ?? '',
      },
      product: {
        ...(hit.product ?? {}),
        freightPrice: hit.product?.freightPrice ?? '',
        productGroupId: hit.productGroup?.id ?? '',
      },
    };
  };

  async getSkusFromVariantIds(variantIds: Array<string>): Promise<ApiResponse<GetSkusResponse>> {
    const normalisedVariantIds = variantIds
      ?.filter(Boolean)
      .map(id => (`${id}`.match(/\D/g) ? id : `gid://shopify/ProductVariant/${id}`));
    const QUERY = `{
      nodes (ids: ${JSON.stringify(normalisedVariantIds)}) {
         id,
        ...on ProductVariant { 
          sku
        }
      }
     }`;
    try {
      const response = await fetch(this._shopifyProductsGqlUrl, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': this._shopifyProductsGqlToken ?? '',
          'Content-Type': 'application/graphql',
        },
        body: QUERY,
      });
      const result = await response.json();
      if (result?.errors || !result.data) {
        return { error: { message: result.errors?.[0]?.message }, statusCode: 500 };
      }
      return {
        data: { products: result.data?.nodes?.filter((node: { id: string; sku: string } | null) => node?.sku) || [] },
        statusCode: 200,
      };
    } catch (err) {
      return {
        error: { message: typeof err === 'string' ? err : err?.toString() ?? 'Unknown error' },
        statusCode: 500,
      };
    }
  }

  async getProductsFromVariantIds(
    variantIds: Array<string>,
    algoliaSearchOptions: AlgoliaSearchOptions = {},
    options: GetProductsOptions = {}
  ): Promise<ApiResponse<GetProductsResponse>> {
    try {
      const getSkusResponse = await this.getSkusFromVariantIds(variantIds);
      if (getSkusResponse.error || !getSkusResponse.data || !getSkusResponse.data.products?.filter(Boolean).length) {
        return Promise.reject(getSkusResponse);
      }

      const productSkus = getSkusResponse.data.products;

      const { data, error, statusCode } = await this.getProductsFromSkus(
        productSkus.map(({ sku }) => sku),
        algoliaSearchOptions,
        options
      );
      if (error || !data || !data.products?.filter(Boolean).length) {
        return Promise.reject({ data, error, statusCode });
      }

      return {
        data: {
          ...data,
          products: data.products?.map(product => ({
            ...product,
            global_variant_id: productSkus.find(({ sku }) => sku === product.sku)?.id,
          })),
        },
        error,
        statusCode,
      };
    } catch (e) {
      return Promise.reject({
        error: {
          message: `Error fetching products from algolia with variant IDs: ${JSON.stringify(variantIds)}`,
          originalError: JSON.stringify(e),
        },
        statusCode: 500,
      });
    }
  }

  async getProductsFromAlgoliaQuery(
    query: string,
    algoliaSearchOptions: AlgoliaSearchOptions = {},
    options: GetProductsOptions = {}
  ): Promise<ApiResponse<GetProductsResponse>> {
    try {
      const algoliaIndex = this._algoliaClient.initIndex(
        `${this._algoliaSearchIndex}${options.sortByMethod ? `_${options.sortByMethod}` : ''}`
      );
      const result = await algoliaIndex.search('', {
        ...algoliaSearchOptions,
        filters: buildSearchFilters(query, {
          shouldExcludeMarketplaceProducts: options.shouldExcludeMarketplaceProducts,
        }),
      });

      const algoliaProductHits = result?.hits?.filter(Boolean)?.map(this.parseAlgoliaHit);

      if (algoliaProductHits) {
        return {
          data: {
            products: options.shouldResolveExtraOnlineProductFields
              ? await this.resolveExtraOnlineProductFields(algoliaProductHits)
              : algoliaProductHits,
            numberOfProducts: result?.nbHits,
          },
          statusCode: 200,
        };
      }
      return { error: { message: 'Error fetching products from algolia with search filter' }, statusCode: 500 };
    } catch (err) {
      return { error: { message: 'Error fetching products from algolia with search filter' }, statusCode: 500 };
    }
  }

  async getProductsFromSkus(
    skus: Array<string>,
    algoliaSearchOptions: AlgoliaSearchOptions = {},
    options: GetProductsOptions = {}
  ): Promise<ApiResponse<GetProductsResponse>> {
    try {
      const uniqueSkus = [...new Set(skus?.filter(Boolean))].map(sku => `(sku:${sku} OR skus:${sku})`);
      const algoliaIndex = this._algoliaClient.initIndex(this._algoliaSearchIndex);
      const result = await algoliaIndex.search('', {
        // hitsPerPage: uniqueSkus.length, removed this to fix a bug, turn back on if it's causing performance issues
        ...algoliaSearchOptions,
        filters: buildSearchFilters(uniqueSkus.join(' OR '), {
          shouldExcludeMarketplaceProducts: options?.shouldExcludeMarketplaceProducts,
        }),
        analytics: true,
        ...(options.analyticsTags?.length && {
          analyticsTags: options.analyticsTags,
        }),
      });
      const algoliaProductHits = result?.hits?.filter(Boolean).map(this.parseAlgoliaHit);

      if (algoliaProductHits) {
        const products = skus
          .map(sku => algoliaProductHits.find(hit => hit.sku === sku || hit.skus?.includes(sku)))
          .filter(Boolean) as Array<AlgoliaProductHit>;
        return {
          data: {
            products: options.shouldResolveExtraOnlineProductFields
              ? await this.resolveExtraOnlineProductFields(products)
              : products,
            numberOfProducts: result?.nbHits,
          },
          statusCode: 200,
        };
      }

      return Promise.reject({ error: { message: 'Error fetching products from algolia with skus' }, statusCode: 500 });
    } catch (err) {
      return Promise.reject({
        error: { message: `Error fetching products from algolia with skus: ${err}` },
        statusCode: 500,
      });
    }
  }
  async getProductRecommendations({
    productSkus,
    modelType = 'RFY',
    source = 'pdp',
    numberOfResults = 12,
    visitorId,
    algoliaSearchOptions = {},
    options = {
      acceptedDeviationPercentage: 20,
      shouldEnhanceSuggestions: false,
    },
    excludedSkus,
  }: {
    productSkus?: Array<string>;
    modelType?: string;
    source?: string;
    numberOfResults?: number;
    visitorId: string;
    algoliaSearchOptions?: AlgoliaSearchOptions;
    options?: GetProductsOptions;
    excludedSkus?: Array<string>;
  }): Promise<ApiResponse<GetProductsResponse>> {
    const { data, error, statusCode } = await this._fetch<{
      results: Array<{ id: string; itemMetadata: { score: number } }>;
    }>({
      path: this._recommendationsEndpoint,
      method: 'POST',
      body: {
        placement: modelType,
        visitor_id: visitorId || null,
        event_type: source === 'pdp' ? 'detail-page-view' : 'home-page-view',
        page_size: numberOfResults,
        product_ids: productSkus,
        validate_only: false,
        return_product: false,
        return_score: true,
      },
    });

    if (error || !data?.results?.length) {
      return { error: error ?? { message: 'getProductRecommendations error' }, statusCode: statusCode };
    }

    const filteredData = options?.shouldEnhanceSuggestions
      ? filterGoogleRecommendations(data?.results, options.acceptedDeviationPercentage)
      : (data?.results as Array<GoogleRecommendedItemWithVariance>);

    const skus = filteredData.map(product => product.id);

    const productsResponse = await this.getProductsFromSkus(
      [...skus, ...(excludedSkus ?? [])],
      { ...algoliaSearchOptions, hitsPerPage: numberOfResults },
      { shouldExcludeMarketplaceProducts: this._shouldExcludeMarketplaceProductsFromSuggestions, ...options }
    );

    return {
      ...productsResponse,
      data: {
        ...productsResponse.data,
        products: (productsResponse.data?.products ?? []).map(product => {
          const matchedGoogleRecommendation = filteredData.find(
            recommendedProduct => recommendedProduct.id === product.sku
          );

          return {
            ...product,
            variance: matchedGoogleRecommendation?.variance,
            varianceScore: matchedGoogleRecommendation?.varianceScore,
            googleScore: matchedGoogleRecommendation?.itemMetadata.score,
          } as ScoredAlgoliaHit;
        }),
        numberOfProducts: filteredData.length,
      },
    };
  }

  async resolveExtraOnlineProductFields(products: Array<AlgoliaProductHit>): Promise<Array<AlgoliaProductHit>> {
    const normalisedVariantIds = products
      ?.filter(p => p.variant_id)
      .map(({ variant_id }) =>
        `${variant_id}`.match(/\D/g) ? variant_id : `gid://shopify/ProductVariant/${variant_id}`
      );

    const onlineProductQuery = `{
      nodes(ids: ${JSON.stringify(normalisedVariantIds)}){
        ...on ProductVariant { 
          product {
            metafield (namespace: "online_product", key: "value"){
              value
            }
          }
        }
      }
    }`;

    try {
      const response = await fetch(this._shopifyProductsGqlUrl, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': this._shopifyProductsGqlToken ?? '',
          'Content-Type': 'application/graphql',
        },
        body: onlineProductQuery,
      });
      const result = await response.json();
      const onlineProducts = (
        (result?.data?.nodes || []) as Array<{
          product: { metafield: { value: string } };
        }>
      ).map(item => toCamelCase(JSON.parse(item.product.metafield.value)));
      return products.map(product => {
        const onlineProduct = onlineProducts.find(p => p.id === product.sku || product.skus?.includes(p.id)) as
          | {
              category: AlgoliaProductHit['onlineProductCategory'];
              display?: { relatedProductIds?: Array<string> };
            }
          | undefined;
        return {
          ...product,
          onlineProductCategory: onlineProduct?.category,
          relatedProductSkus: onlineProduct?.display?.relatedProductIds || [],
        };
      });
    } catch (err) {
      return products;
    }
  }

  static getAlogliaCache = (appId: string, durationInSeconds: number = 60) => {
    return {
      responsesCache: createFallbackableCache({
        caches: [
          createBrowserLocalStorageCache({
            key: `algolia-responses-${appId}`,
            timeToLive: durationInSeconds,
          }),
          createInMemoryCache(),
        ],
      }),
    };
  };
}
